/**
 * What the table offers right now: every object you can act on, what acting
 * on it does, and the few turn controls that belong to no object — skip, end
 * turn, confirm, cancel.
 *
 * Built from `describeCommands`, which is `legalCommands` with words on it, so
 * the table cannot offer a move the engine would refuse, and cannot hide one it
 * would allow: every legal command is the act of some target or button, or is
 * reached by picking things up first — a tray's markers, cards for a ship, a
 * card for the discard. `offer.test.ts` walks those routes in every state a
 * game reaches and checks both directions.
 */
import {
  MANIFESTS,
  MARKERS_PER_TRAY,
  decisionMaker,
  previewTray,
  type ActionKind,
  type Command,
  type ConstructionActionPayload,
  type ForumActionPayload,
  type MilitaryActionPayload,
  type PlayerId,
  type PlayerView,
  type SeaportActionPayload,
  type TrajanActionPayload,
  type TrayIndex,
  type View,
} from '@trajan/engine';
import { planPlacement } from '../mat/placement';
import { describeCommands, prompt, trajanName, type Option } from '../panel/describe';
import {
  COMMODITY_TYPES,
  EMPTY_SELECTION,
  planSeaport,
  toggleCard,
  toggleWild,
} from '../panel/seaport';
import { START_SLOTS, chooseSlot, isComplete, pickStack, slotOf, toPicks } from '../setup/tilePlan';
import type { TargetId } from '../table/ids';
import { NONE, holdCards, pickTray, settle, tapMarker, type Holding } from './holding';

/** Acting on a target either sends a command or changes what is held. */
export type Act = { send: Command } | { hold: Holding };

export type Tone = 'go' | 'choose' | 'picked';

export interface Target {
  id: TargetId;
  act: Act;
  title: string;
  detail?: string;
  vp?: number;
  /** `go` sends a move; `choose` picks something up; `picked` is held (acting puts it back). */
  tone: Tone;
}

export interface Button {
  key: string;
  label: string;
  /** Null while the button cannot be pressed yet. */
  act: Act | null;
  primary?: boolean;
  detail?: string;
}

/** A small menu hung from a target, for the one choice with no place on the table. */
export interface Chooser {
  anchor: TargetId;
  title: string;
  options: { key: string; label: string; act: Act }[];
}

export interface Offer {
  /** What the player holds, as the phase allows it. */
  holding: Holding;
  /** True when the game is waiting for this seat. */
  yours: boolean;
  title: string;
  hint: string;
  targets: Target[];
  buttons: Button[];
  chooser: Chooser | null;
  /** Things to mark without making them live: the demand being answered. */
  focus: TargetId[];
  /** Cards lifted out of the hand. */
  raised: Set<string>;
  /** What each ship would pay for the cards chosen, while some are. */
  ships: { shipId: string; vp: number | null }[] | null;
}

type Words = Pick<Option, 'title'> & Partial<Pick<Option, 'detail' | 'vp'>>;
type Add = (id: TargetId, act: Act, words: Words, tone?: Tone) => void;

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];
const SLOT_BESIDE = MANIFESTS.trajanSlotBesideTray as TrayIndex[];
const trayOf = (action: ActionKind): number => MANIFESTS.trayActions.indexOf(action);
const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function offer(view: View, seat: PlayerId | null, raw: Holding): Offer {
  const base = prompt(view);
  const out: Offer = {
    holding: NONE,
    yours: false,
    title: base.title,
    hint: base.hint,
    targets: [],
    buttons: [],
    chooser: null,
    focus: [],
    raised: new Set(),
    ships: null,
  };
  if (view.phase.kind === 'quarterEnd.demands') out.focus.push(`demand:${view.phase.demandIndex}`);
  const player = seat === null ? undefined : view.players[seat];
  if (seat === null || !player || decisionMaker(view) !== seat) return out;

  const holding = settle(view, seat, raw);
  out.holding = holding;
  out.yours = true;
  const options = describeCommands(view, seat);

  const add: Add = (id, act, words, tone = 'go') => {
    out.targets.push({
      id,
      act,
      title: words.title,
      ...(words.detail !== undefined ? { detail: words.detail } : {}),
      ...(words.vp !== undefined ? { vp: words.vp } : {}),
      tone,
    });
  };
  const turnButtons = () => {
    for (const o of options) {
      if (o.command.type !== 'SkipAction' && o.command.type !== 'EndTurn') continue;
      out.buttons.push({
        key: o.key,
        label: o.title,
        act: { send: o.command },
        ...(o.detail !== undefined ? { detail: o.detail } : {}),
      });
    }
  };

  const phase = view.phase;
  switch (phase.kind) {
    case 'setup.arrangeMarkers': {
      if (holding.kind !== 'arrange') break;
      holding.layout.forEach((tray, t) =>
        tray.forEach((color, k) => {
          const index = t * MARKERS_PER_TRAY + k;
          const action = MANIFESTS.trayActions[t]!;
          add(
            `marker:${index}`,
            { hold: tapMarker(holding, index) },
            { title: markerTitle(holding.held, index, color, action, heldColour(holding)) },
            holding.held === index ? 'picked' : 'choose',
          );
        }),
      );
      out.buttons.push({
        key: 'arrange',
        label: 'Start with this layout',
        act: { send: { type: 'ArrangeMarkers', trays: holding.layout.map((t) => [...t]) } },
        primary: true,
      });
      out.buttons.push({
        key: 'dealt',
        label: 'As dealt',
        act: sameLayout(holding.layout, player.trays)
          ? null
          : { hold: { kind: 'arrange', layout: player.trays.map((t) => [...t]), held: null } },
      });
      out.hint =
        'Two markers per tray, two of each colour. Drag one onto another to swap them, or tap one and then the other.';
      break;
    }

    case 'setup.chooseTrajanTiles': {
      if (holding.kind !== 'tiles') break;
      const plan = holding.plan;
      const placed = Object.keys(plan.placed).length;
      view.board.trajanStacks.forEach((stack, i) => {
        const top = stack[0];
        if (!top) return;
        const kind = trajanName(MANIFESTS.tiles.trajanStacks[i] ?? `stack ${i + 1}`);
        const on = slotOf(plan, i);
        const state = plan.holding === i ? 'held' : on !== null ? 'placed' : 'free';
        // One per category: a fourth stack is shut once three are placed, but a
        // placed tile can always be picked back up.
        if (state === 'free' && placed >= 3 && plan.holding === null) return;
        add(
          `stack:${i}`,
          { hold: { kind: 'tiles', plan: pickStack(plan, i) } },
          {
            title:
              state === 'held'
                ? `Put the ${kind} tile back`
                : state === 'placed'
                  ? `Lift the ${kind} tile off slot ${ROMAN[on!]}`
                  : `Take the ${kind} tile`,
            detail: `needs ${top.colors[0]} + ${top.colors[1]} · ${top.vp} VP`,
          },
          state === 'free' ? 'choose' : 'picked',
        );
      });
      for (const s of START_SLOTS) {
        const there = plan.placed[s];
        if (plan.holding === null && there === undefined) continue;
        add(
          `slot:${s}`,
          { hold: { kind: 'tiles', plan: chooseSlot(plan, s) } },
          {
            title:
              plan.holding === null
                ? `Lift the tile off slot ${ROMAN[s]}`
                : there === undefined
                  ? `Put the tile on slot ${ROMAN[s]}`
                  : `Put it on slot ${ROMAN[s]}, and take that one up`,
            detail: `beside the ${MANIFESTS.trayActions[s]} tray`,
          },
          'choose',
        );
      }
      const picks = toPicks(plan);
      out.buttons.push({
        key: 'take-tiles',
        label: 'Take these tiles',
        act: picks ? { send: { type: 'ChooseInitialTrajanTiles', picks } } : null,
        primary: true,
      });
      const inHand =
        plan.holding === null ? null : trajanName(MANIFESTS.tiles.trajanStacks[plan.holding] ?? '');
      out.hint = inHand
        ? `The ${inHand} tile is in hand: put it on slot II, IV or VI of your mat.`
        : isComplete(plan)
          ? 'All three are down. Move one by choosing it again, or take them.'
          : 'Take three tiles from three different stacks, and put each on slot II, IV or VI.';
      break;
    }

    case 'awaitTray': {
      if (holding.kind === 'tray') {
        const plan = planPlacement(player, holding.tray, holding.order, SLOT_BESIDE);
        if (!plan) break;
        for (const d of plan.drops) {
          add(
            `marker:${d.index}`,
            { hold: tapMarker(holding, d.index) },
            {
              title: markerTitle(
                holding.held,
                d.index,
                d.color,
                MANIFESTS.trayActions[d.tray]!,
                heldColour(holding),
              ),
              ...(d.last ? { detail: 'lands last, so it decides the action' } : {}),
            },
            holding.held === d.index ? 'picked' : 'choose',
          );
        }
        const action = MANIFESTS.trayActions[plan.target]!;
        const slot = SLOT_BESIDE[plan.target]!;
        out.buttons.push({
          key: 'take-action',
          label: `Take the ${action} action`,
          act: { send: { type: 'SelectTray', tray: holding.tray, colorOrder: [...holding.order] } },
          primary: true,
          detail: `time ${plan.moved}${plan.completesTarget ? ` · completes slot ${ROMAN[slot]}` : ''}`,
        });
        out.buttons.push({ key: 'cancel', label: 'Put them back', act: { hold: NONE } });
        out.title = capitalise(action);
        out.hint =
          'The last marker to land decides the action. Swap markers to change which tiles they complete.';
        break;
      }
      for (const o of options) {
        if (o.command.type !== 'SelectTray') continue;
        const t = o.command.tray;
        add(
          `tray:${t}`,
          { hold: pickTray(view, seat, t) },
          { title: o.title, detail: trayDetail(player, t) },
          'choose',
        );
      }
      break;
    }

    case 'awaitTrajanDecision': {
      const slot = phase.slot;
      const tile = player.trajanSlots[slot];
      const accepts = options.filter(
        (o) => o.command.type === 'AccomplishTrajanTile' && o.command.accept,
      );
      const decline = options.find(
        (o) => o.command.type === 'AccomplishTrajanTile' && !o.command.accept,
      );
      if (holding.kind === 'plus2') {
        add(`slot:${slot}`, { hold: NONE }, { title: 'Back to the tile' }, 'picked');
        for (const o of accepts) {
          if (o.command.type !== 'AccomplishTrajanTile' || !o.command.plus2Space) continue;
          add(`plus2:${o.command.plus2Space}`, { send: o.command }, o);
        }
        out.hint = 'Choose the extra-action space for the “+2” marker.';
      } else if (tile?.kind === 'plus2' && accepts.length > 0) {
        add(
          `slot:${slot}`,
          { hold: { kind: 'plus2' } },
          {
            title: 'Accomplish the “+2” tile',
            detail: 'then choose the extra-action space its marker goes on',
            vp: tile.vp,
          },
          'choose',
        );
      } else if (accepts[0]) {
        add(`slot:${slot}`, { send: accepts[0].command }, accepts[0]);
      }
      if (decline) {
        out.buttons.push({
          key: decline.key,
          label: 'Leave the tile',
          act: { send: decline.command },
          ...(decline.detail !== undefined ? { detail: decline.detail } : {}),
        });
      }
      break;
    }

    case 'awaitAction': {
      for (const o of options) {
        const c = o.command;
        if (c.type !== 'TakeAction') continue;
        switch (c.kind) {
          case 'forum': {
            const f = c.payload as ForumActionPayload;
            add(f.from === 'yellow' ? `yellow:${f.index}` : `forum:${f.index}`, { send: c }, o);
            break;
          }
          case 'military': {
            const m = c.payload as MilitaryActionPayload;
            if (m.option === 'deploy') add('camp:legion', { send: c }, o);
            else if (m.option === 'move') add(`province:${m.province}`, { send: c }, o);
            else if (player.leader !== 'camp') add(`province:${player.leader}`, { send: c }, o);
            break;
          }
          case 'senate':
            add(`senate:${player.senate + 1}`, { send: c }, o);
            break;
          case 'trajan':
            add(`stack:${(c.payload as TrajanActionPayload).stack}`, { send: c }, o);
            break;
          case 'construction': {
            const k = c.payload as ConstructionActionPayload;
            add(k.option === 'deploy' ? 'camp:workers' : `site:${k.site}`, { send: c }, o);
            break;
          }
          case 'seaport': {
            const s = c.payload as SeaportActionPayload;
            if (s.option === 'draw') add('deck', { send: c }, o);
            else if (s.option === 'takeDiscard') add(`pile:${s.pile}`, { send: c }, o);
            break;
          }
        }
      }
      if (options.some((o) => o.owner === 'builder'))
        seaportCards(view, seat, player, holding, out, add);
      turnButtons();
      break;
    }

    case 'awaitSeaportDiscard': {
      for (const card of player.hand ?? []) {
        if (holding.kind === 'discard' && holding.cardId === card.id) {
          add(`card:${card.id}`, { hold: NONE }, { title: `Keep the ${card.type}` }, 'picked');
          out.raised.add(card.id);
        } else {
          add(
            `card:${card.id}`,
            { hold: { kind: 'discard', cardId: card.id } },
            { title: `Discard the ${card.type}`, detail: 'then choose a pile' },
            'choose',
          );
        }
      }
      if (holding.kind === 'discard') {
        for (const o of options) {
          if (o.command.type === 'DiscardCard' && o.command.cardId === holding.cardId) {
            add(`pile:${o.command.pile}`, { send: o.command }, o);
          }
        }
        out.hint = 'Now the pile it goes on. It lies face up, where anyone may take it.';
      } else {
        out.hint = 'Choose a card to discard, then a pile.';
      }
      break;
    }

    case 'awaitExtraAction': {
      const wilds = new Map<string, Option[]>();
      for (const o of options) {
        const c = o.command;
        if (c.type !== 'UseExtraActionTile') continue;
        if (c.action === undefined) add(`held:${c.tileId}`, { send: c }, o);
        else wilds.set(c.tileId, [...(wilds.get(c.tileId) ?? []), o]);
      }
      for (const [tileId, list] of wilds) {
        if (holding.kind === 'wild' && holding.tileId === tileId) {
          add(`held:${tileId}`, { hold: NONE }, { title: 'Keep the wild tile' }, 'picked');
          for (const o of list) {
            if (o.command.type !== 'UseExtraActionTile' || !o.command.action) continue;
            add(`tray:${trayOf(o.command.action)}`, { send: o.command }, o);
          }
        } else {
          add(
            `held:${tileId}`,
            { hold: { kind: 'wild', tileId } },
            {
              title: 'Spend the wild extra-action tile',
              detail: 'then choose the action to repeat, on your mat',
            },
            'choose',
          );
        }
      }
      if (holding.kind === 'wild') {
        out.hint = 'Choose the action to repeat: the tray of one you took this turn.';
      }
      turnButtons();
      break;
    }

    case 'quarterEnd.demands':
      for (const o of options) {
        if (o.command.type === 'SatisfyDemand')
          add(`held:${o.command.tileId}`, { send: o.command }, o);
      }
      break;

    case 'quarterEnd.consulChoice':
      for (const o of options) {
        if (o.command.type === 'ChooseBonusTile')
          add(`bonus:${o.command.side}`, { send: o.command }, o);
      }
      break;

    case 'gameOver':
      break;
  }
  return out;
}

/** Choosing cards at the seaport: choose cards, then a ship pays for them or your display takes them. */
function seaportCards(
  view: View,
  seat: PlayerId,
  player: PlayerView,
  holding: Holding,
  out: Offer,
  add: Add,
) {
  const selection = holding.kind === 'cards' ? holding.selection : EMPTY_SELECTION;
  for (const card of player.hand ?? []) {
    const on = selection.cardIds.includes(card.id);
    add(
      `card:${card.id}`,
      { hold: holdCards(toggleCard(selection, card.id)) },
      on
        ? { title: `Put the ${card.type} back` }
        : {
            title: `Choose the ${card.type}`,
            detail: 'for a set to ship, or one or two to display',
          },
      on ? 'picked' : 'choose',
    );
    if (on) out.raised.add(card.id);
  }
  for (const t of player.forumTiles) {
    if (t.kind !== 'wild-commodity') continue;
    const used = selection.wilds.find((w) => w.tileId === t.id);
    const asking = holding.kind === 'cards' && holding.wildFor === t.id;
    add(
      `held:${t.id}`,
      used
        ? { hold: holdCards(toggleWild(selection, t.id, used.asType)) }
        : asking
          ? { hold: holdCards(selection) }
          : { hold: { kind: 'cards', selection, wildFor: t.id } },
      used
        ? { title: `Take the wild tile out — it stands in for ${used.asType}` }
        : {
            title: 'Use the wild tile as any commodity',
            detail: 'it stands in for one card in a set you ship',
          },
      used || asking ? 'picked' : 'choose',
    );
  }
  if (holding.kind === 'cards' && holding.wildFor) {
    const tileId = holding.wildFor;
    out.chooser = {
      anchor: `held:${tileId}`,
      title: 'The wild tile stands in for',
      options: COMMODITY_TYPES.map((type) => ({
        key: type,
        label: type,
        act: { hold: holdCards(toggleWild(selection, tileId, type)) },
      })),
    };
  }
  const plan = planSeaport(view, seat, selection);
  if (plan.chosen.length === 0) {
    out.hint =
      holding.kind === 'cards' && holding.wildFor
        ? 'Choose the commodity the wild tile stands in for.'
        : 'Draw from the deck, take a discard, or choose cards to ship or put on display.';
    return;
  }
  out.ships = plan.ships.map((s) => ({ shipId: s.shipId, vp: s.units === null ? null : s.vp }));
  for (const s of plan.ships) {
    if (!s.command) continue;
    add(
      `ship:${s.shipId}`,
      { send: s.command },
      { title: `Ship them on the ${s.set} ship`, detail: s.why, vp: s.vp },
    );
  }
  if (plan.display) {
    const n = plan.display.cards;
    add(
      'display',
      { send: plan.display.command },
      {
        title: `Put ${n === 1 ? 'this card' : 'these two cards'} on display`,
        detail: `draw ${n} to replace ${n === 1 ? 'it' : 'them'}; displayed cards score at the end`,
      },
    );
  }
  out.buttons.push({ key: 'clear', label: 'Clear the cards', act: { hold: holdCards() } });
  out.hint =
    holding.kind === 'cards' && holding.wildFor
      ? 'Choose the commodity the wild tile stands in for.'
      : `${plan.chosen.length} chosen. Each ship shows what it would pay; your display takes one or two.`;
}

function markerTitle(
  held: number | null,
  index: number,
  color: string,
  action: string,
  heldColor: string | null,
): string {
  if (held === null) return `Lift the ${color} marker in the ${action} tray`;
  if (held === index) return `Put the ${color} marker back down`;
  return `Swap the ${heldColor ?? 'lifted'} marker with this ${color} one`;
}

function heldColour(holding: Holding): string | null {
  if (holding.kind === 'tray' && holding.held !== null) return holding.order[holding.held] ?? null;
  if (holding.kind === 'arrange' && holding.held !== null) {
    return (
      holding.layout[Math.floor(holding.held / MARKERS_PER_TRAY)]?.[
        holding.held % MARKERS_PER_TRAY
      ] ?? null
    );
  }
  return null;
}

const sameLayout = (a: readonly (readonly string[])[], b: readonly (readonly string[])[]) =>
  a.length === b.length &&
  a.every((t, i) => t.length === (b[i]?.length ?? -1) && t.every((c, k) => c === b[i]![k]));

/** Where a tray's markers would take you, in a phrase. */
export function trayDetail(player: PlayerView, tray: number): string {
  const p = previewTray(player, tray as TrayIndex, SLOT_BESIDE);
  if (!p) return 'empty';
  const target = MANIFESTS.trayActions[p.target]!;
  const tile = p.trajanTile
    ? p.trajanTile.satisfied
      ? ` · completes slot ${ROMAN[p.trajanTile.slot]}`
      : ` · slot ${ROMAN[p.trajanTile.slot]} would still be short`
    : '';
  return `lands on ${capitalise(target)} · time ${p.moved}${tile}`;
}
